import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Separator } from '@nesso/ui/primitives/separator';

import { BillingHistory } from '@/entities/billing-history';
import {
    formatPaymentMethod,
    formatPaymentNumber,
    formatRefundNumber,
    type Payment,
} from '@/entities/payment';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericDate } from '@/shared/lib/time-utils';

type Props = {
    payment: Payment;
    isCompact: boolean;
    getServiceLabel: (serviceCode: string) => string;
    onOpenAppliedTo: (paymentId: string, sessionChargeId: string) => void;
    onRefund: (payment: Payment) => void;
};

export function ClientBillingSheetActivityPaymentDetails({
    payment,
    isCompact,
    getServiceLabel,
    onOpenAppliedTo,
    onRefund,
}: Props) {
    const number = formatPaymentNumber(payment.number);

    return (
        <div className="flex flex-col gap-4 text-sm">
            <section className="flex flex-col gap-1">
                <h4 className="font-bold">Applied to</h4>
                <ul className="flex flex-col items-start gap-1.5">
                    {payment.appliedTo.map((appliedTo) => (
                        <li
                            key={appliedTo.sessionChargeId}
                            className={cn(
                                payment.refund &&
                                    'flex w-full items-center justify-between line-through',
                            )}
                        >
                            <Button
                                type="button"
                                variant="inline-link"
                                onClick={() =>
                                    onOpenAppliedTo(payment.id, appliedTo.sessionChargeId)
                                }
                            >
                                {formatNumericDate(appliedTo.date)},{' '}
                                {getServiceLabel(appliedTo.serviceCode)}
                            </Button>
                            {payment.refund && (
                                <span>{formatCurrencyFromCents(appliedTo.amountCents)}</span>
                            )}
                        </li>
                    ))}
                    {payment.refund && (
                        <li className="flex w-full items-center justify-between">
                            <span className="underline decoration-from-font underline-offset-auto [text-decoration-skip-ink:none] [text-underline-position:from-font]">
                                {formatRefundNumber(payment.refund.number)}
                            </span>
                            <span>{formatCurrencyFromCents(-payment.refund.amountCents)}</span>
                        </li>
                    )}
                </ul>
            </section>
            {isCompact && (
                <section className="flex flex-col gap-1">
                    <h4 className="font-bold">Method</h4>
                    <p>{formatPaymentMethod(payment.method, payment.checkNumber)}</p>
                </section>
            )}
            <Separator />
            <section className="flex flex-col gap-2">
                <h4 className="font-bold">History</h4>
                <BillingHistory entries={payment.history} />
            </section>
            {!payment.refund && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 self-start"
                    aria-label={`Refund ${number}`}
                    onClick={() => onRefund(payment)}
                >
                    Refund
                </Button>
            )}
        </div>
    );
}
